import { useEffect, useState, useCallback, useRef } from 'react';
import { type LicenseTier } from './useLicense';
import { useLicense } from './useLicense';

export interface Entitlement {
  feature_key: string;
  label: string;
  granted: boolean;
  required_tier: string;
}

interface EntitlementsResponse {
  entitlements: Entitlement[];
  tier: string;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

function hashLicenseKey(licenseKey: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < licenseKey.length; i += 1) {
    const ch = licenseKey.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const combined = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return combined.toString(36);
}

export function getEntitlementsCacheKey(licenseKey: string): string {
  return `indxone.entitlements.v1.${hashLicenseKey(licenseKey)}`;
}

interface CacheEntry {
  data: EntitlementsResponse;
  cachedAt: number;
  licenseKeyHash: string;
}

function getLocalTierLevel(tier: LicenseTier, kind?: string | null): number {
  if (kind === 'owner') return 3;
  if (tier === 'pro') return 2;
  if (tier === 'starter') return 1;
  return 0;
}

const LOCAL_FEATURES: Array<{ key: string; requiredLevel: number }> = [
  { key: 'tool_eval', requiredLevel: 0 },
  { key: 'tool_mission_canvas', requiredLevel: 1 },
  { key: 'tool_sipoc', requiredLevel: 0 },
  { key: 'tool_moscow', requiredLevel: 0 },
  { key: 'tool_roles', requiredLevel: 0 },
  { key: 'tool_raci', requiredLevel: 0 },
  { key: 'tool_deal_room', requiredLevel: 1 },
  { key: 'tool_daily_ops', requiredLevel: 0 },
  { key: 'tool_client_risk', requiredLevel: 0 },
  { key: 'tool_calc_mission', requiredLevel: 0 },
  { key: 'tool_goal', requiredLevel: 0 },
  { key: 'tool_project', requiredLevel: 0 },
  { key: 'tool_scratchpad', requiredLevel: 1 },
  { key: 'tool_notes', requiredLevel: 1 },
  { key: 'tool_tjm_radar', requiredLevel: 0 },
  { key: 'tool_missio', requiredLevel: 1 },
  { key: 'tool_cra', requiredLevel: 1 },
  { key: 'tool_chrono', requiredLevel: 1 },
  { key: 'tool_mission_memory', requiredLevel: 1 },
  { key: 'tool_tresorerie', requiredLevel: 1 },
  { key: 'tool_runway', requiredLevel: 1 },
  { key: 'tool_projections', requiredLevel: 2 },
  { key: 'tool_fiscal', requiredLevel: 2 },
  { key: 'tool_benchmark', requiredLevel: 2 },
  { key: 'tool_reporting', requiredLevel: 2 },
  { key: 'tool_estimation', requiredLevel: 1 },
  { key: 'tool_proposal', requiredLevel: 1 },
  { key: 'tool_devis', requiredLevel: 1 },
  { key: 'tool_profile', requiredLevel: 0 },
  { key: 'tool_stack_registry', requiredLevel: 0 },
  { key: 'tool_admin_licenses', requiredLevel: 3 },
  { key: 'cloud_backup', requiredLevel: 1 },
  { key: 'export_pdf', requiredLevel: 0 },
  { key: 'export_csv', requiredLevel: 1 },
];

function computeLocalFallback(tier: LicenseTier, kind?: string | null): Entitlement[] {
  const level = getLocalTierLevel(tier, kind);
  const labelMap: Record<string, string> = {
    tool_eval: 'Évaluer une opportunité', tool_mission_canvas: 'Cadrage mission',
    tool_sipoc: 'Périmètre', tool_moscow: 'Priorités',
    tool_roles: 'Rôles', tool_raci: 'Matrice RACI',
    tool_deal_room: 'Partage client', tool_daily_ops: 'Pilotage du jour',
    tool_client_risk: 'Évaluation client', tool_calc_mission: 'Chiffrer une mission',
    tool_goal: 'Mon TJM', tool_project: 'Projet forfait',
    tool_scratchpad: 'Éditeur de formules', tool_notes: 'Notes',
    tool_tjm_radar: 'Comparer au marché', tool_missio: 'Cockpit missions',
    tool_cra: 'Agenda', tool_chrono: 'Chrono',
    tool_mission_memory: 'Retours missions', tool_tresorerie: 'Trésorerie',
    tool_runway: 'Réserve', tool_projections: 'Projections',
    tool_fiscal: 'Optimisation fiscale', tool_benchmark: 'Comparer au marché (détaillé)',
    tool_reporting: 'Reporting', tool_estimation: 'Chiffrage détaillé',
    tool_proposal: 'Proposition', tool_devis: 'Devis',
    tool_profile: 'Profil', tool_stack_registry: 'Référentiels outils',
    tool_admin_licenses: 'Admin licences',
    cloud_backup: 'Sauvegarde cloud', export_pdf: 'Export PDF',
    export_csv: 'Export CSV',
  };
  const tierNames: Record<number, string> = { 0: 'free', 1: 'starter', 2: 'pro', 3: 'owner' };
  return LOCAL_FEATURES.map((f) => ({
    feature_key: f.key,
    label: labelMap[f.key] || f.key,
    granted: level >= f.requiredLevel,
    required_tier: tierNames[f.requiredLevel] || 'free',
  }));
}

function readCache(licenseKey: string): EntitlementsResponse | null {
  try {
    const raw = localStorage.getItem(getEntitlementsCacheKey(licenseKey));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(getEntitlementsCacheKey(licenseKey));
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache(licenseKey: string, data: EntitlementsResponse): void {
  try {
    const entry: CacheEntry = { data, cachedAt: Date.now(), licenseKeyHash: hashLicenseKey(licenseKey) };
    localStorage.setItem(getEntitlementsCacheKey(licenseKey), JSON.stringify(entry));
  } catch {
    // noop
  }
}

export function useEntitlements() {
  const license = useLicense();
  const [entitlements, setEntitlements] = useState<Entitlement[]>(() => {
    const key = license.key;
    if (key) {
      const cached = readCache(key);
      if (cached) return cached.entitlements;
    }
    return computeLocalFallback(license.tier, license.kind);
  });
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const fetchEntitlements = useCallback(async () => {
    const key = license.key;
    if (!key) {
      setEntitlements(computeLocalFallback(license.tier, license.kind));
      return;
    }

    const cached = readCache(key);
    if (cached) {
      setEntitlements(cached.entitlements);
      return;
    }

    setLoading(true);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/api/me/entitlements', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Indxone-License-Key': key,
        },
        signal: controller.signal,
      }).finally(() => clearTimeout(timeout));

      if (!response.ok) {
        setEntitlements(computeLocalFallback(license.tier, license.kind));
        return;
      }

      const data = (await response.json()) as EntitlementsResponse;
      if (!mountedRef.current) return;
      writeCache(key, data);
      setEntitlements(data.entitlements);
    } catch {
      if (!mountedRef.current) return;
      const cached = readCache(key);
      if (cached) {
        setEntitlements(cached.entitlements);
      } else {
        setEntitlements(computeLocalFallback(license.tier, license.kind));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [license.key, license.tier, license.kind]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const isFeatureEnabled = useCallback(
    (featureKey: string): boolean => {
      const found = entitlements.find((e) => e.feature_key === featureKey);
      if (found) return found.granted;
      const local = LOCAL_FEATURES.find((f) => f.key === featureKey);
      if (local) {
        const level = getLocalTierLevel(license.tier, license.kind);
        return level >= local.requiredLevel;
      }
      return false;
    },
    [entitlements, license.tier, license.kind],
  );

  return { entitlements, loading, isFeatureEnabled, refresh: fetchEntitlements };
}
